import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { SajuResult } from '@/utils/sajuCalculations';
import { calculateSaju } from '@/utils/sajuCalculations';
import { analyzeDongup, type DongupAnalysis, type DongupRadar } from '@/utils/dongupEngine';
import { analyzeDongupEn } from '@/utils/dongupEngineEn';

interface Props {
  userResult: SajuResult;
  initialNameA?: string;
  onBack: () => void;
  onClose: () => void;
}

// ── Radar Chart ─────────────────────────────────────────────────
function Radar({ r, isEn }: { r: DongupRadar; isEn: boolean }) {
  const cx = 100, cy = 100, maxR = 72;
  const angles = [270, 342, 54, 126, 198].map(a => (a * Math.PI) / 180);
  const labels = isEn
    ? ['Wealth\nSynergy', 'Role\nFit', 'Comm\nEnergy', 'Values\nMatch', 'Long-term\nStability']
    : ['재물\n시너지', '역할\n보완성', '소통\n에너지', '가치관\n일치도', '장기\n안정성'];
  const vals = [r.money, r.role, r.comm, r.values, r.stability];
  const dataPts = angles.map((a, i) => {
    const rv = (vals[i] / 100) * maxR;
    return `${cx + rv * Math.cos(a)},${cy + rv * Math.sin(a)}`;
  }).join(' ');
  return (
    <svg viewBox="0 0 200 200" className="w-full max-w-[220px] mx-auto">
      {[0.25, 0.5, 0.75, 1].map(scale => (
        <polygon key={scale} points={angles.map(a => `${cx + maxR * scale * Math.cos(a)},${cy + maxR * scale * Math.sin(a)}`).join(' ')}
          fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.8" />
      ))}
      {angles.map((a, i) => (
        <line key={i} x1={cx} y1={cy} x2={cx + maxR * Math.cos(a)} y2={cy + maxR * Math.sin(a)} stroke="rgba(255,255,255,0.08)" strokeWidth="0.8" />
      ))}
      <polygon points={dataPts} fill="rgba(245,158,11,0.15)" stroke="#f59e0b" strokeWidth="1.5" />
      {angles.map((a, i) => {
        const rv = (vals[i] / 100) * maxR;
        return <circle key={i} cx={cx + rv * Math.cos(a)} cy={cy + rv * Math.sin(a)} r="2.5" fill="#f59e0b" />;
      })}
      {angles.map((a, i) => {
        const lr = maxR + 20;
        const lx = cx + lr * Math.cos(a);
        const ly = cy + lr * Math.sin(a);
        return (
          <text key={i} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fontSize="8" fill="rgba(255,255,255,0.35)">
            {labels[i].split('\n').map((ln, j) => (
              <tspan key={j} x={lx} dy={j === 0 ? -4 : 10}>{ln}</tspan>
            ))}
          </text>
        );
      })}
    </svg>
  );
}

function Sec({ num, title }: { num: string; title: string }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <span className="text-[10px] text-amber-400/40 font-mono tracking-widest whitespace-nowrap">§ {num}</span>
      <div className="h-px flex-1 bg-white/8" />
      <h3 className="text-sm font-black text-white/90 whitespace-nowrap">{title}</h3>
    </div>
  );
}

function scoreColor(v: number) {
  return v >= 80 ? '#f59e0b' : v >= 65 ? '#4ade80' : v >= 50 ? '#94a3b8' : '#f87171';
}

// ── Input Component ────────────────────────────────────────────
function NameInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={10}
      className="w-full bg-white/8 border border-white/15 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 outline-none focus:border-amber-400/50 transition-colors text-center"
    />
  );
}

export default function DongupReport({ userResult, initialNameA = '', onBack, onClose }: Props) {
  const { i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  const [stage, setStage] = useState<'input' | 'result'>('input');
  const [nameA, setNameA] = useState(initialNameA);
  const [nameB, setNameB] = useState('');
  const [genderB, setGenderB] = useState<'male' | 'female'>('male');
  const [yearB, setYearB] = useState(1990);
  const [monthB, setMonthB] = useState(1);
  const [dayB, setDayB] = useState(1);
  const [analysis, setAnalysis] = useState<DongupAnalysis | null>(null);
  const [error, setError] = useState('');
  const [shared, setShared] = useState(false);

  const displayA = nameA.trim() || (isEn ? 'Person A' : '나');
  const displayB = nameB.trim() || (isEn ? 'Partner' : '파트너');

  const handleCalc = useCallback(() => {
    try {
      if (!yearB || !monthB || !dayB) {
        setError(isEn ? 'Please enter all birth date fields.' : '생년월일을 모두 입력해주세요.');
        return;
      }
      if (yearB < 1920 || yearB > 2010) {
        setError(isEn ? 'Please enter a valid birth year.' : '올바른 출생연도를 입력해주세요.');
        return;
      }
      const partnerResult = calculateSaju(yearB, monthB, dayB, 12, 0, genderB, 'solarDate', null);
      const result = isEn
        ? analyzeDongupEn(userResult, partnerResult, displayA, displayB)
        : analyzeDongup(userResult, partnerResult, displayA, displayB);
      setAnalysis(result);
      setError('');
      setStage('result');
    } catch {
      setError(isEn ? 'An error occurred. Please check the birth date.' : '계산 중 오류가 발생했습니다. 생년월일을 다시 확인해주세요.');
    }
  }, [userResult, displayA, displayB, genderB, yearB, monthB, dayB, isEn]);

  const handleShare = useCallback(async () => {
    if (!analysis) return;
    const shareText = isEn
      ? `🤝 [CLASSIFIED] BarunSaju Partnership Analysis\n\n${displayA} × ${displayB}\nScore: ${analysis.score} (${analysis.scoreLabel})\nDay Branch: ${analysis.dayBranchLabel}\n\nAnalyze your partnership 👉 ${window.location.href}`
      : `🤝 [CLASSIFIED] 바른사주 동업 분석\n\n${displayA} × ${displayB}\n동업 점수: ${analysis.score}점 (${analysis.scoreLabel})\n일지 관계: ${analysis.dayBranchLabel}\n\n동업 궁합 분석하러 가기 👉 ${window.location.href}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: isEn ? `${displayA} × ${displayB} Partnership Analysis` : `${displayA} × ${displayB} 동업 분석`,
          text: shareText,
          url: window.location.href,
        });
        setShared(true);
        setTimeout(() => setShared(false), 2500);
        return;
      } catch { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(shareText);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = shareText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setShared(true);
    setTimeout(() => setShared(false), 2500);
  }, [analysis, displayA, displayB, isEn]);

  // ── Common Header ────────────────────────────────────────────
  const Header = ({ onLeftClick, showShare = false }: { onLeftClick: () => void; showShare?: boolean }) => (
    <div
      className="sticky top-0 z-10 bg-[#080808]/96 backdrop-blur border-b border-white/8 px-4 flex items-center gap-3"
      style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}
    >
      <button onClick={onLeftClick} className="w-7 h-7 flex items-center justify-center border border-white/10 rounded text-white/40 hover:text-white/70 cursor-pointer transition-colors flex-none">
        <i className="ri-arrow-left-line text-sm" />
      </button>
      <div className="flex items-center gap-1.5 flex-1 min-w-0">
        <i className="ri-lock-line text-amber-400 text-xs flex-none" />
        {stage === 'result' && analysis ? (
          <p className="text-amber-400 text-[10px] tracking-wide font-bold truncate">
            {displayA} × {displayB} {isEn ? 'Partnership' : '동업 분석'}
          </p>
        ) : (
          <p className="text-amber-400 text-[10px] tracking-[2px] font-bold">
            CLASSIFIED · {isEn ? 'Partnership Analysis' : '동업 분석'}
          </p>
        )}
      </div>
      {showShare && (
        <button
          onClick={handleShare}
          className={`w-7 h-7 flex items-center justify-center border rounded transition-all cursor-pointer flex-none ${shared ? 'border-amber-400/50 bg-amber-400/10' : 'border-white/10 hover:border-amber-400/40 hover:bg-amber-400/5'}`}
        >
          {shared
            ? <i className="ri-check-line text-amber-400 text-sm" />
            : <i className="ri-share-forward-line text-white/40 text-sm" />
          }
        </button>
      )}
      <button onClick={onClose} className="w-7 h-7 flex items-center justify-center border border-white/10 rounded text-white/40 hover:text-white/70 cursor-pointer transition-colors flex-none">
        <i className="ri-close-line text-sm" />
      </button>
    </div>
  );

  // ── Input Form ─────────────────────────────────────────────────
  if (stage === 'input') {
    return (
      <div className="fixed inset-0 z-[60] bg-[#080808] flex flex-col min-h-dvh">
        <Header onLeftClick={onBack} showShare={false} />
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-lg mx-auto px-4 pt-5" style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}>
            {/* Title */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-amber-400/10 border border-amber-400/30 px-4 py-1.5 rounded-full mb-3">
                <span className="text-amber-400 text-xs font-bold tracking-wider">
                  {isEn ? '🤝 Business Partnership Analysis' : '🤝 비즈니스 동업 정밀 분석'}
                </span>
              </div>
              <h2 className="text-xl font-black text-white mb-2" style={{ fontFamily: "'Noto Serif KR', serif" }}>
                {isEn ? 'Enter both names\nand information' : '두 사람의 이름과 정보를\n입력해주세요'}
              </h2>
              <p className="text-white/30 text-xs">
                {isEn ? 'Enter names for a fully personalized report' : '이름을 입력하면 완전히 개인화된 리포트가 나옵니다'}
              </p>
            </div>

            {/* VS Card Layout */}
            <div className="relative mb-5">
              <div className="grid grid-cols-2 gap-3">
                {/* A Card */}
                <div className="bg-amber-400/5 border border-amber-400/20 rounded-2xl p-4">
                  <p className="text-[10px] text-amber-400/50 font-mono mb-2 text-center">
                    {isEn ? 'PERSON A · You' : 'PERSON A · 나'}
                  </p>
                  <NameInput
                    value={nameA}
                    onChange={setNameA}
                    placeholder={isEn ? 'Your name (optional)' : '내 이름 (선택)'}
                  />
                  <div className="mt-3 pt-3 border-t border-amber-400/10 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center bg-amber-400/10 rounded-full flex-none">
                        <i className="ri-user-star-line text-amber-400 text-[10px]" />
                      </div>
                      <p className="text-sm font-black text-white" style={{ fontFamily: "'Noto Serif KR', serif" }}>
                        {userResult.dayPillar.stem}{userResult.dayPillar.branch}
                      </p>
                    </div>
                    <p className="text-[10px] text-white/35 pl-7">
                      {isEn ? 'Analyzed Day Pillar' : '기분석된 일주'}
                    </p>
                    <p className="text-[10px] text-amber-400/40 pl-7 font-mono">
                      {userResult.yearPillar.stem}{userResult.yearPillar.branch}&nbsp;
                      {userResult.monthPillar.stem}{userResult.monthPillar.branch}&nbsp;
                      {userResult.dayPillar.stem}{userResult.dayPillar.branch}
                    </p>
                  </div>
                </div>

                {/* B Card */}
                <div className="bg-white/4 border border-white/10 rounded-2xl p-4">
                  <p className="text-[10px] text-white/30 font-mono mb-2 text-center">
                    {isEn ? 'PERSON B · Partner' : 'PERSON B · 파트너'}
                  </p>
                  <NameInput
                    value={nameB}
                    onChange={setNameB}
                    placeholder={isEn ? 'Partner name (optional)' : '상대 이름 (선택)'}
                  />
                  <div className="mt-3 pt-3 border-t border-white/8 space-y-2.5">
                    {/* Gender */}
                    <div className="grid grid-cols-2 gap-1.5">
                      {(['male', 'female'] as const).map(g => (
                        <button key={g} onClick={() => setGenderB(g)}
                          className={`py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${genderB === g ? 'bg-white/15 border border-white/30 text-white' : 'bg-white/5 border border-white/8 text-white/35'}`}>
                          {g === 'male' ? (isEn ? 'Male' : '남성') : (isEn ? 'Female' : '여성')}
                        </button>
                      ))}
                    </div>
                    {/* Birth Date */}
                    <div>
                      <p className="text-[10px] text-white/25 mb-1.5">
                        {isEn ? 'Birth Date (Solar)' : '생년월일 (양력)'}
                      </p>
                      <div className="grid grid-cols-3 gap-1">
                        {[
                          { label: isEn ? 'Year' : '년', val: yearB, set: setYearB, min: 1920, max: 2010, ph: '1990' },
                          { label: isEn ? 'Month' : '월', val: monthB, set: setMonthB, min: 1, max: 12, ph: '1' },
                          { label: isEn ? 'Day' : '일', val: dayB, set: setDayB, min: 1, max: 31, ph: '1' },
                        ].map(({ label, val, set, min, max, ph }) => (
                          <div key={label}>
                            <p className="text-[9px] text-white/20 mb-0.5 text-center">{label}</p>
                            <input type="number" value={val} onChange={e => set(Number(e.target.value))}
                              min={min} max={max} placeholder={ph}
                              className="w-full bg-white/8 border border-white/12 rounded-lg px-1.5 py-2 text-xs text-white text-center outline-none focus:border-amber-400/50 transition-colors" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* VS Badge */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-[#080808] border border-white/15 rounded-full z-10">
                <span className="text-[9px] font-black text-white/40">VS</span>
              </div>
            </div>

            {/* Name Preview */}
            {(nameA.trim() || nameB.trim()) && (
              <div className="bg-amber-400/5 border border-amber-400/15 rounded-xl px-4 py-3 mb-4 text-center">
                <p className="text-xs text-white/50">
                  <span className="text-amber-400 font-bold">{displayA}</span>
                  <span className="text-white/20 mx-2">×</span>
                  <span className="text-white/70 font-bold">{displayB}</span>
                  <span className="text-white/30 ml-2">{isEn ? 'Partnership' : '동업 분석'}</span>
                </p>
              </div>
            )}

            {error && <p className="text-red-400 text-xs text-center mb-4">{error}</p>}

            <button onClick={handleCalc}
              className="w-full bg-amber-400/15 hover:bg-amber-400/25 border border-amber-400/50 hover:border-amber-400 text-amber-400 font-black py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-sm">
              <i className="ri-lock-unlock-line text-base" />
              {isEn ? 'Start Partnership Analysis' : '동업 정밀 분석 시작'}
              <i className="ri-arrow-right-line text-sm" />
            </button>
            <p className="text-center text-white/12 text-[10px] mt-3 font-mono">
              {isEn ? 'Information is not saved and used only for immediate calculation' : '입력 정보는 저장되지 않으며 즉시 계산에만 사용됩니다'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!analysis) return null;
  const oc = scoreColor(analysis.score);

  // ── Report ──────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[60] bg-[#080808] overflow-y-auto">
      <Header onLeftClick={() => setStage('input')} showShare={true} />

      <div className="max-w-lg mx-auto px-4 py-6 space-y-8">

        {/* Report Title Banner */}
        <div className="bg-amber-400/5 border border-amber-400/20 rounded-2xl p-5 text-center">
          <p className="text-[10px] text-amber-400/40 font-mono mb-2 tracking-widest">
            CLASSIFIED · {isEn ? 'Partnership Analysis Report' : '동업 정밀 분석 리포트'}
          </p>
          <div className="flex items-center justify-center gap-3 mb-1">
            <div className="text-center">
              <p className="text-base font-black text-amber-400" style={{ fontFamily: "'Noto Serif KR', serif" }}>{displayA}</p>
              <p className="text-[10px] text-white/35 font-mono">{analysis.personA.label.split(' ')[0]}</p>
            </div>
            <div className="flex flex-col items-center gap-0.5">
              <div className="h-px w-6 bg-white/15" />
              <span className="text-[10px] text-white/30 font-bold">×</span>
              <div className="h-px w-6 bg-white/15" />
            </div>
            <div className="text-center">
              <p className="text-base font-black text-white/80" style={{ fontFamily: "'Noto Serif KR', serif" }}>{displayB}</p>
              <p className="text-[10px] text-white/35 font-mono">{analysis.personB.label.split(' ')[0]}</p>
            </div>
          </div>
          <p className="text-[10px] text-white/20 font-mono mt-2">
            {isEn ? '2026 Bǐngwǔ Year Partnership Synergy Analysis' : '2026년 병오년 동업 시너지 분석'}
          </p>
        </div>

        {/* Score Card */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white/4 border border-white/10 rounded-xl p-4">
            <p className="text-white/30 text-[10px] mb-1">
              {isEn ? 'Partnership Score' : '동업 점수'}
            </p>
            <p className="text-4xl font-black" style={{ color: oc }}>{analysis.score}</p>
            <p className="text-xs font-black mt-0.5" style={{ color: oc }}>{analysis.scoreLabel}</p>
          </div>
          <div className="bg-white/4 border border-white/10 rounded-xl p-4">
            <p className="text-white/30 text-[10px] mb-1">
              {isEn ? 'Day Master Relation' : '일간 관계'}
            </p>
            <p className="text-sm font-black text-white/80 mb-0.5">{displayA} → {analysis.sipAB}</p>
            <p className="text-sm font-black text-white/55">{displayB} → {analysis.sipBA}</p>
          </div>
        </div>

        {/* Radar */}
        <div>
          <Sec num="01" title={isEn ? '5 Partnership Metrics' : '5가지 동업 능력치'} />
          <div className="bg-white/4 border border-white/10 rounded-xl p-5">
            <Radar r={analysis.radar} isEn={isEn} />
            <div className="grid grid-cols-5 gap-1 mt-4">
              {(isEn
                ? [['Wealth', analysis.radar.money], ['Role', analysis.radar.role], ['Comm', analysis.radar.comm], ['Values', analysis.radar.values], ['Stability', analysis.radar.stability]]
                : [['재물', analysis.radar.money], ['역할', analysis.radar.role], ['소통', analysis.radar.comm], ['가치관', analysis.radar.values], ['안정성', analysis.radar.stability]]
              ).map(([label, val]) => (
                <div key={label as string} className="text-center">
                  <p className="text-[10px] font-black" style={{ color: scoreColor(val as number) }}>{val}</p>
                  <p className="text-[9px] text-white/30">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Overall Review */}
        <div>
          <Sec num="02" title={isEn ? 'Overall Review & Relationship Analysis' : '총평 및 관계 분석'} />
          <div className="bg-amber-400/4 border border-amber-400/20 rounded-xl p-5 mb-3">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] text-amber-400/50 font-mono">
                {isEn ? 'Summary' : '총평'}
              </span>
              <div className="h-px flex-1 bg-amber-400/10" />
            </div>
            <p className="text-sm text-amber-400/85 font-black mb-3 leading-snug" style={{ fontFamily: "'Noto Serif KR', serif" }}>
              {analysis.overallTitle}
            </p>
            <p className="text-sm text-white/60 leading-relaxed">{analysis.overallText}</p>
          </div>
          <div className="bg-white/4 border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-xs font-black text-white/60">
                {isEn ? 'Day Branch Relation' : '일지 관계'}
              </span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                analysis.dayBranchLabel.includes('합') ? 'border-green-500/40 text-green-400 bg-green-500/10' :
                analysis.dayBranchLabel.includes('충') ? 'border-red-500/40 text-red-400 bg-red-500/10' :
                (analysis.dayBranchLabel.includes('파') || analysis.dayBranchLabel.includes('해')) ? 'border-orange-500/40 text-orange-400 bg-orange-500/10' :
                'border-white/15 text-white/40 bg-white/5'
              }`}>{analysis.dayBranchLabel}</span>
            </div>
            <p className="text-xs text-white/50 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: analysis.branchBizText.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white/80">$1</strong>') }} />
          </div>
        </div>

        {/* Role Division */}
        <div>
          <Sec num="03" title={`${displayA} × ${displayB} ${isEn ? 'Role Division' : '역할 분담'}`} />
          <div className="space-y-3">
            {[
              { name: displayA, role: analysis.roleA, isUser: true, pillar: analysis.personA.label.split(' ')[0] },
              { name: displayB, role: analysis.roleB, isUser: false, pillar: analysis.personB.label.split(' ')[0] },
            ].map((p, i) => (
              <div key={i} className={`rounded-xl p-4 border ${p.isUser ? 'bg-amber-400/5 border-amber-400/20' : 'bg-white/4 border-white/10'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <i className={`${p.isUser ? 'ri-user-star-line text-amber-400' : 'ri-user-line text-white/40'} text-sm`} />
                  <p className="text-sm font-black" style={{ color: p.isUser ? '#f59e0b' : 'rgba(255,255,255,0.65)' }}>
                    {p.name}{isEn ? '' : '님'}
                  </p>
                  <span className="text-[10px] text-white/20 font-mono ml-auto">{p.pillar}</span>
                </div>
                <p className="text-xs font-bold text-white/70 mb-1.5">{p.role.title}</p>
                <p className="text-xs text-white/45 leading-relaxed">{p.role.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 2026 Timing */}
        <div>
          <Sec num="04" title={isEn ? '2026 Bǐngwǔ Year Partnership Timing' : '2026년 병오년 동업 타이밍'} />
          <div className="space-y-3 mb-3">
            {[
              { label: isEn ? `2026 for ${displayA}` : `${displayA}님에게 2026년은`, text: analysis.timing2026.a },
              { label: isEn ? `2026 for ${displayB}` : `${displayB}님에게 2026년은`, text: analysis.timing2026.b },
            ].map((t, i) => (
              <div key={i} className="bg-white/4 border border-white/10 rounded-xl p-4">
                <p className="text-[10px] text-amber-400/50 font-mono mb-2">{t.label}</p>
                <p className="text-xs text-white/55 leading-relaxed">{t.text}</p>
              </div>
            ))}
          </div>
          <div className="bg-amber-400/4 border border-amber-400/15 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-rocket-line text-amber-400 text-sm" />
              <p className="text-xs font-black text-white/70">
                {isEn ? `${displayA} + ${displayB} Combined Analysis` : `${displayA} + ${displayB} 합산 분석`}
              </p>
            </div>
            <p className="text-xs text-white/55 leading-relaxed">{analysis.timing2026.combined}</p>
          </div>
        </div>

        {/* Risks */}
        <div>
          <Sec num="05" title={isEn ? 'BarunSaju Reality Check' : '바른사주 팩폭 조언'} />
          <div className="space-y-3">
            {analysis.risks.map((risk, i) => (
              <div key={i} className="border border-red-500/15 bg-red-500/4 rounded-xl p-4">
                <div className="flex items-start gap-2 mb-2">
                  <i className="ri-alarm-warning-line text-red-400/70 text-sm flex-none mt-0.5" />
                  <p className="text-xs font-black text-white/70">{risk.title}</p>
                </div>
                <p className="text-xs text-white/45 leading-relaxed pl-5">{risk.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="pt-2 space-y-3" style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}>
          <div className="h-px bg-white/8" />
          <p className="text-center text-white/10 text-[10px] font-mono">
            CLASSIFIED · {displayA} × {displayB} {isEn ? 'Partnership Analysis Report' : '동업 분석 리포트'}
          </p>
          <button onClick={onBack}
            className="w-full border border-white/15 text-white/40 hover:text-white/65 hover:border-white/25 py-3.5 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap">
            {isEn ? 'Back to Annual Report' : '신년운세 리포트로 돌아가기'}
          </button>
          <button onClick={() => { setNameB(''); setStage('input'); }}
            className="w-full border border-amber-400/20 text-amber-400/60 hover:border-amber-400/40 hover:text-amber-400/80 py-3.5 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap">
            {isEn ? 'Analyze with Different Partner' : '다른 파트너로 재분석'}
          </button>
        </div>
      </div>
    </div>
  );
}